import { toaster } from "@/components/ui/toaster";
import { getGrossMarginRequests } from "@/services/gross-margin-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormSetValue } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import { GrossMarginRequestsListResponse } from "./gross-margin-requests.schema";

interface FetchGrossMarginRequestsHandlerProps {
  filters?: any;
  showLoad?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setList: Dispatch<SetStateAction<GrossMarginRequestsListResponse | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

/** Obtiene la lista paginada de solicitudes aplicando los filtros activos. */
export const fetchGrossMarginRequestsHandler = async ({
  filters,
  showLoad = true,
  setLoading,
  setList,
  navigate,
  fetchPermissions,
}: FetchGrossMarginRequestsHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getGrossMarginRequests({
      Cuenta: filters?.cuentaOrigen?.value
        ? Number(filters.cuentaOrigen.value)
        : undefined,
      Cultivos: filters?.Cultivos,
      Estados: filters?.Estados,
      Campanas: filters?.Campanas,
      Fecha: filters?.Fecha,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setList(response ?? undefined);
  } catch (error) {
    const errorObj = error as AxiosError;

    const handled = await handlePermissionError(error, navigate, fetchPermissions);
    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudieron obtener las solicitudes de margen bruto.",
        ),
      });
    }

    throw error;
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitGrossMarginFiltersHandlerProps {
  event: FieldValues;
  setPage: Dispatch<SetStateAction<number>>;
  fetchList: (filters?: any) => Promise<void>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  filtersEmpty: boolean;
}

/**
 * Aplica los filtros del formulario: normaliza arrays de IDs numéricos,
 * resetea la paginación a página 1 y cierra el panel de filtros.
 */
export const submitGrossMarginFiltersHandler = async ({
  event,
  setPage,
  fetchList,
  setCurrentFilters,
  setFiltersApplied,
  setOpenFilters,
  filtersEmpty,
}: SubmitGrossMarginFiltersHandlerProps) => {
  const cultivos = Array.isArray(event?.cultivos)
    ? event.cultivos.map(Number).filter((v: number) => !Number.isNaN(v))
    : undefined;

  const estados = Array.isArray(event?.estados)
    ? event.estados.map(Number).filter((v: number) => !Number.isNaN(v))
    : undefined;

  const campanas = Array.isArray(event?.campanas)
    ? event.campanas.map(Number).filter((v: number) => !Number.isNaN(v))
    : undefined;

  const nextFilters = {
    cuentaOrigen: event?.cuentaOrigen || undefined,
    Cultivos: cultivos?.length ? cultivos : undefined,
    Estados: estados?.length ? estados : undefined,
    Campanas: campanas?.length ? campanas : undefined,
    Fecha: event?.fecha || undefined,
    "P.Pagina": 1,
  };

  setPage(1);
  setCurrentFilters(nextFilters);
  setFiltersApplied(!filtersEmpty);
  setOpenFilters(false);

  await fetchList(nextFilters);
};

interface ClearGrossMarginFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setPage: Dispatch<SetStateAction<number>>;
  setCurrentFilters: Dispatch<SetStateAction<any>>;
  fetchList: (filters?: any) => Promise<void>;
}

export const clearGrossMarginFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  setOpenFilters,
  setPage,
  setCurrentFilters,
  fetchList,
}: ClearGrossMarginFiltersHandlerProps) => {
  setValue("cuentaOrigen", undefined);
  setValue("cultivos", []);
  setValue("estados", []);
  setValue("campanas", []);
  setValue("fecha", undefined);

  setFiltersApplied(false);
  setOpenFilters(false);
  setPage(1);
  setCurrentFilters(undefined);

  await fetchList({ "P.Pagina": 1 });
};
